import { useQuery } from "@tanstack/react-query";
import LeadStats from "../components/dashboard/LeadStats";
import RevenueChart from "../components/dashboard/RevenueChart";
import ActivityFeed from "../components/dashboard/ActivityFeed";
import { leadService } from "../services/leads";
import { clientService } from "../services/clients";
import { projectService } from "../services/projects";
import { taskService } from "../services/tasks";
import { Users, UserCircle, CheckSquare, Target } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, subtext, color }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-shadow">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const Dashboard = () => {
  const { data: leads } = useQuery({ queryKey: ["leads"], queryFn: leadService.getAll });
  const { data: clients } = useQuery({ queryKey: ["clients"], queryFn: clientService.getAll });
  const { data: projects } = useQuery({ queryKey: ["projects"], queryFn: projectService.getAll });
  const { data: tasks } = useQuery({ queryKey: ["tasks"], queryFn: taskService.getAll });

  const totalLeads = leads?.length || 0;
  const converted = leads?.filter((l) => l.status === "converted").length || 0;
  const hotLeads = leads?.filter((l) => l.leadScore === "hot").length || 0;
  const totalClients = clients?.length || 0;
  const activeProjects = projects?.filter((p) => p.status === "in_progress").length || 0;
  const totalTasks = tasks?.length || 0;
  const completedTasks = tasks?.filter((t) => t.completed).length || 0;

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Welcome back! Here's what's happening.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard icon={Users} label="Total Leads" value={totalLeads} subtext={`${hotLeads} hot leads`} color="bg-gradient-to-br from-blue-500 to-blue-600" />
        <StatCard icon={Target} label="Converted" value={converted} subtext={totalLeads ? `${((converted / totalLeads) * 100).toFixed(1)}% rate` : "0%"} color="bg-gradient-to-br from-emerald-500 to-emerald-600" />
        <StatCard icon={UserCircle} label="Clients" value={totalClients} subtext={`${activeProjects} active projects`} color="bg-gradient-to-br from-purple-500 to-purple-600" />
        <StatCard icon={CheckSquare} label="Tasks" value={totalTasks} subtext={`${completedTasks} completed`} color="bg-gradient-to-br from-amber-500 to-orange-500" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <RevenueChart />
        </div>
        <LeadStats />
      </div>
      <ActivityFeed />
    </div>
  );
};

export default Dashboard;
